import React, {useCallback, useMemo} from 'react';
import {Box, Tooltip, useMediaQuery, useTheme} from '@mui/material';
import {useLocalization} from 'localization';
import {useShortcut} from 'services/shortcuts';
import {ToolBar} from 'components';
import {useWorkSizeContext} from '../contexts';

export const WorkSizeTools: React.FC = () => {
  const {t} = useLocalization();
  const {workSize, setWorkSize} = useWorkSizeContext();
  const theme = useTheme();
  const smOrSmaller = useMediaQuery(theme.breakpoints.down('md'));

  const onToggle = useCallback(() => {
    switch (workSize) {
      case 'minimized':
        setWorkSize('maximized');
        return;
      case 'maximized':
        setWorkSize(undefined);
        return;
      default:
        setWorkSize('minimized');
    }
  }, [setWorkSize, workSize]);

  useShortcut('toggleWork', onToggle);

  const onShrink = useCallback(() => {
    if (workSize !== 'minimized') {
      setWorkSize(workSize === 'maximized' ? undefined : 'minimized');
    }
  }, [setWorkSize, workSize]);

  const onGrow = useCallback(() => {
    if (workSize !== 'maximized') {
      setWorkSize(workSize === 'minimized' ? undefined : 'maximized');
    }
  }, [setWorkSize, workSize]);

  const {shrinkTooltip, growTooltip} = useMemo(() => {
    switch (workSize) {
      case 'minimized':
        return {
          shrinkTooltip: '',
          growTooltip: t('page.metadata.work.size.showWork'),
        };
      case 'maximized':
        return {
          shrinkTooltip: t('page.metadata.work.size.showExpressions'),
          growTooltip: '',
        };
      default:
        return {
          shrinkTooltip: t('page.metadata.work.size.maximizeExpressions'),
          growTooltip: t('page.metadata.work.size.maximizeWork'),
        };
    }
  }, [t, workSize]);

  if (smOrSmaller) {
    return null;
  }

  return (
    <>
      <Tooltip title={growTooltip} arrow>
        <Box sx={{display: 'flex', alignItems: 'center'}}>
          <ToolBar.IconTool
            icon={'PanelLeft'}
            disabled={workSize === 'maximized'}
            onClick={onGrow}
          />
        </Box>
      </Tooltip>
      <Tooltip title={shrinkTooltip} arrow>
        <Box sx={{display: 'flex', alignItems: 'center'}}>
          <ToolBar.IconTool
            icon={'PanelRight'}
            disabled={workSize === 'minimized'}
            onClick={onShrink}
          />
        </Box>
      </Tooltip>
    </>
  );
};
